import { DateTime } from 'luxon';
import { useAgencyDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/store/agencyDashboard.store';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { KpiStatCardProps } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCard';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';
import { buildClearedCard } from 'pages/ReportsPage/components/AnalyticsDashboards/kpiCardBuilders';

function formatPriorRange(rangeStart: string, periodDays: number | null): string | undefined {
  if (periodDays == null || periodDays <= 0) return undefined;
  const start = DateTime.fromISO(rangeStart, { setZone: true });
  if (!start.isValid) return undefined;
  const priorStart = start.minus({ days: periodDays });
  const priorEnd = start.minus({ days: 1 });
  const fmt = (d: DateTime) => d.toFormat('MMM d');
  return `vs. ${fmt(priorStart)} – ${fmt(priorEnd)}`;
}

export function useAgencyKpiCards(): KpiStatCardProps[] {
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const rangeStart = useAnalyticsFilterStore((s) => s.dateRange[0]);
  const totalHours = useAgencyDashboardStore((s) => s.totalHours);
  const totalHoursPeriodDays = useAgencyDashboardStore((s) => s.totalHoursPeriodDays);
  const totalHoursChangePrct = useAgencyDashboardStore((s) => s.totalHoursChangePrct);
  const totalHoursError = useAgencyDashboardStore((s) => s.totalHoursError);
  const totalShifts = useAgencyDashboardStore((s) => s.totalShifts);
  const totalShiftsPeriodDays = useAgencyDashboardStore((s) => s.totalShiftsPeriodDays);
  const totalShiftsChangePrct = useAgencyDashboardStore((s) => s.totalShiftsChangePrct);
  const totalShiftsError = useAgencyDashboardStore((s) => s.totalShiftsError);
  const sentToAgencyRate = useAgencyDashboardStore((s) => s.sentToAgencyRate);
  const sentToAgencyRatePeriodDays = useAgencyDashboardStore((s) => s.sentToAgencyRatePeriodDays);
  const sentToAgencyRateChangePrct = useAgencyDashboardStore((s) => s.sentToAgencyRateChangePrct);
  const sentToAgencyRateError = useAgencyDashboardStore((s) => s.sentToAgencyRateError);

  const totalHoursComparison = formatPriorRange(rangeStart, totalHoursPeriodDays);
  const totalShiftsComparison = formatPriorRange(rangeStart, totalShiftsPeriodDays);
  const sentToAgencyRateComparison = formatPriorRange(rangeStart, sentToAgencyRatePeriodDays);

  const cards: KpiStatCardProps[] = [
    totalHoursError
      ? {
          label: 'Total agency hours',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Total agency hours',
          value: formatDashboardNumber(totalHours),
          ...(totalHoursChangePrct != null && {
            changePercentage: totalHoursChangePrct,
            comparisonText: totalHoursComparison,
          }),
        },
    sentToAgencyRateError
      ? {
          label: 'Sent to agency rate',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Sent to agency rate',
          value: `${formatDashboardNumber(sentToAgencyRate)}%`,
          ...(sentToAgencyRateChangePrct != null && {
            changePercentage: sentToAgencyRateChangePrct,
            comparisonText: sentToAgencyRateComparison,
          }),
        },
    totalShiftsError
      ? {
          label: 'Agency shifts',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Agency shifts',
          value: totalShifts.toLocaleString(),
          ...(totalShiftsChangePrct != null && {
            changePercentage: totalShiftsChangePrct,
            comparisonText: totalShiftsComparison,
          }),
        },
  ];

  return isSelectionCleared(locationIds, positions)
    ? cards.map((card) => buildClearedCard(card.label))
    : cards;
}
